
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import usePlan from "../hooks/usePlan";
import OfflineBanner from "../components/OfflineBanner";
import { fetchWithFallback } from "../utils/fetchWithFallback";
import { useTheme } from "../context/ThemeContext";

export default function Dashboard() {
  const { theme } = useTheme();
  const [summary, setSummary] = useState(null);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [trial, setTrial] = useState(null);
  const { hasFullAccess, isExpired } = usePlan();
  const [hasSales, setHasSales] = useState(false);
  const [categoriesCount, setCategoriesCount] = useState(0);
  const [expenseSummary, setExpenseSummary] = useState(null);
  const [isPC, setIsPC] = useState(window.innerWidth >= 768);
  const [debtSummary, setDebtSummary] = useState(null);


  // Shared dynamic styles — defined once, used everywhere
  const cardStyle = {
    background: theme.cardBg,
    border: `1px solid ${theme.cardBorder}`,
    borderRadius: "12px",
    padding: "16px",
    boxShadow: theme.cardShadow,
  };

  useEffect(() => {
    const check = () => setIsPC(window.innerWidth >= 768);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const setupSteps = [
    { label: "Add your first product", done: summary?.inventory?.total_products > 0 },
    { label: "Record your first sale", done: hasSales },
    { label: "Create a category", done: categoriesCount > 0 },
    { label: "Subscribe to a plan", done: trial?.status === "subscribed" },
  ];

  const setupDone = setupSteps.filter(s => s.done).length;
  const setupPercent = Math.round((setupDone / setupSteps.length) * 100);
  const setupComplete = setupPercent === 100;

  const fetchData = async () => {
    setLoading(true);
    const [summaryRes, lowStockRes, trialRes, salesRes, categoriesRes, expenseRes, debtSummaryRes] = await Promise.all([
      fetchWithFallback("/reports/summary", "ss_cache_dashboard_summary"),
      fetchWithFallback("/products/alerts/low-stock", "ss_cache_dashboard_lowstock"),
      fetchWithFallback("/subscriptions/trial-status", "ss_cache_trial_status"),
      fetchWithFallback("/sales/", "ss_cache_sales"),
      fetchWithFallback("/categories/", "ss_cache_categories"),
      fetchWithFallback("/expenses/summary", "ss_cache_expenses_summary"),
      fetchWithFallback("/debts/summary", "ss_cache_debts_summary"),
    ]);
    if (summaryRes.data) setSummary(summaryRes.data);
    if (lowStockRes.data) setLowStock(lowStockRes.data);
    if (trialRes.data) setTrial(trialRes.data);
    if (salesRes.data) setHasSales(salesRes.data.length > 0);
    if (categoriesRes.data) setCategoriesCount(categoriesRes.data.length);
    if (expenseRes.data) setExpenseSummary(expenseRes.data);
    if (debtSummaryRes.data) setDebtSummary(debtSummaryRes.data);
    setLoading(false);
    
  };

  useEffect(() => { fetchData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleSync = () => { fetchData(); };
    window.addEventListener("martsense-sync-complete", handleSync);
    return () => window.removeEventListener("martsense-sync-complete", handleSync);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const todayNetProfit = (summary?.today?.total_profit ?? 0) - (expenseSummary?.today ?? 0);
  const monthNetProfit = (summary?.this_month?.total_profit ?? 0) - (expenseSummary?.this_month ?? 0);
  const lowStockCount = lowStock?.length ?? 0;

  return (
    <Layout>
      <>
        <OfflineBanner />

        {trial?.status === "trial" && (
          <p style={{ fontSize: "13px", color: theme.textSecondary, marginBottom: "20px" }}>
            🕐 {trial.days_left} days left on your free trial —{" "}
            <Link to="/subscription" style={{ color: "#2ec4b6", fontWeight: 700 }}>Upgrade now</Link>
          </p>
        )}

        {trial?.status === "expired" && (
          <p style={{ fontSize: "13px", color: "#e74c3c", marginBottom: "20px" }}>
            ⚠️ Trial expired —{" "}
            <Link to="/subscription" style={{ color: "#e74c3c", fontWeight: 700 }}>
              Subscribe to continue
            </Link>
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "32px" }}>
          <div>
            <h1 style={{ fontSize: "28px", fontWeight: "700", color: theme.textPrimary, letterSpacing: "-0.5px" }}>
              Dashboard
            </h1>
            <p style={{ fontSize: "14px", color: theme.textSecondary, marginTop: "4px" }}>
              Here is what is happening in your business today
            </p>
          </div>
        </div>

        {loading ? (
          <p style={{ color: theme.textSecondary }}>Loading...</p>
        ) : (
          <>
            {/* Business Setup */}
            {!setupComplete && (
              <div style={{
                background: theme.cardBg,
                border: `1px solid ${theme.cardBorder}`,
                borderRadius: "14px",
                padding: "24px",
                marginBottom: "24px",
                boxShadow: theme.cardShadow,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                  <div>
                    <p style={{ fontSize: "16px", fontWeight: "700", color: theme.textPrimary, marginBottom: "4px" }}>
                      Business Setup
                    </p>
                    <p style={{ fontSize: "13px", color: theme.textSecondary }}>
                      Complete setup to get the most from MartSense
                    </p>
                  </div>
                  <p style={{ fontSize: "28px", fontWeight: "800", color: "#2ec4b6" }}>{setupPercent}%</p>
                </div>
                <div style={{ background: theme.divider, borderRadius: "10px", height: "8px", marginBottom: "20px", overflow: "hidden" }}>
                  <div style={{
                    height: "100%", borderRadius: "10px",
                    background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                    width: `${setupPercent}%`, transition: "width 0.5s ease",
                  }} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  {setupSteps.map((step, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <span style={{
                        width: "24px", height: "24px", borderRadius: "50%",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: "11px", fontWeight: "700", flexShrink: 0,
                        background: step.done ? "#2ecc71" : theme.divider,
                        color: step.done ? "#0a0f0f" : theme.textSecondary,
                      }}>
                        {step.done ? "✓" : i + 1}
                      </span>
                      <span style={{
                        fontSize: "14px",
                        color: step.done ? theme.textSecondary : theme.textPrimary,
                        textDecoration: step.done ? "line-through" : "none",
                      }}>
                        {step.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* KPI Cards */}
            <div style={{ marginBottom: "32px" }}>

              {/* TODAY */}
              <h2
  style={{
    fontSize: "14px",
    fontWeight: "700",
    color: theme.textPrimary,
    marginBottom: "12px",
  }}
>
  TODAY
</h2>
              <div style={{
                display: "grid",
                gridTemplateColumns: isPC ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
                gap: "12px",
                marginBottom: "24px",
              }}>
                <div style={cardStyle}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Revenue</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: "#2ec4b6", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{(summary?.today?.total_revenue ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>earned today</p>
                </div>

                <div style={cardStyle}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Gross Profit</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: "#2ecc71", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{(summary?.today?.total_profit ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>before expenses</p>
                </div>

                <div style={cardStyle}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Net Profit</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: todayNetProfit >= 0 ? "#2ecc71" : "#e74c3c", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{todayNetProfit.toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>profit minus expenses</p>
                </div>
              </div>

              {/* THIS MONTH */}
              <h2
  style={{
    fontSize: "14px",
    fontWeight: "700",
    color: theme.textPrimary,
    marginBottom: "12px",
  }}
>
  THIS MONTH
</h2>
              <div style={{
                display: "grid",
                gridTemplateColumns: isPC ? "repeat(3, 1fr)" : "repeat(2, 1fr)",
                gap: "12px",
              }}>
                <div style={cardStyle}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Monthly Revenue</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: "#2ec4b6", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{(summary?.this_month?.total_revenue ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>this month</p>
                </div>

                <div style={cardStyle}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Monthly Net Profit</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: monthNetProfit >= 0 ? "#2ecc71" : "#e74c3c", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{monthNetProfit.toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>profit minus expenses</p>
                </div>

                <div style={{
                  ...cardStyle,
                  border: (debtSummary?.total_outstanding ?? 0) > 0 ? "1px solid #e74c3c55" : `1px solid ${theme.cardBorder}`,
                }}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Outstanding Debts</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: (debtSummary?.total_outstanding ?? 0) > 0 ? "#e74c3c" : "#2ecc71", marginBottom: "2px", lineHeight: "1.2" }}>
                    ₦{Number(debtSummary?.total_outstanding ?? 0).toLocaleString()}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>
                    {debtSummary?.total_debtors ?? 0} debtors
                  </p>
                </div>

                <div style={{
                  ...cardStyle,
                  border: lowStockCount > 0 ? "1px solid #e74c3c55" : `1px solid ${theme.cardBorder}`,
                  background: lowStockCount > 0 ? "#1a0a0a" : theme.cardBg,
                }}>
                  <p style={{ fontSize: "11px", color: theme.textSecondary, fontWeight: "600", marginBottom: "6px" }}>Low Stock Alerts</p>
                  <p style={{ fontSize: "22px", fontWeight: "800", color: lowStockCount > 0 ? "#e74c3c" : "#2ecc71", marginBottom: "2px", lineHeight: "1.2" }}>
                    {lowStockCount}
                  </p>
                  <p style={{ fontSize: "11px", color: theme.textMuted }}>
                    {lowStockCount > 0 ? "products need restocking" : "all stock levels ok"}
                  </p>
                </div>
              </div>
            </div>

            {/* Low Stock Table */}
            {hasFullAccess && lowStock.length > 0 && (
              <div style={{ marginBottom: "40px" }}>
                <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "16px" }}>
                  ⚠️ Low Stock Products
                </h2>
                <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch", borderRadius: "10px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "400px" }}>
                    <thead>
                      <tr>
                        {["Product", "SKU", "In Stock", "Threshold"].map((h) => (
                          <th key={h} style={{
                            textAlign: "left", padding: "12px 16px",
                            fontSize: "12px", fontWeight: "600",
                            color: theme.textSecondary, textTransform: "uppercase",
                            letterSpacing: "0.5px", borderBottom: `1px solid ${theme.divider}`,
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {lowStock.map((item) => (
                        <tr key={item.sku} style={{ borderBottom: `1px solid ${theme.divider}` }}>
                          <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>{item.name}</td>
                          <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>{item.sku}</td>
                          <td style={{ padding: "14px 16px", fontSize: "14px", color: "#e74c3c", fontWeight: 600 }}>
                            {item.quantity_in_stock}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "14px", color: theme.textPrimary }}>
                            {item.low_stock_threshold}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Upgrade Box */}
            {isExpired && (
              <div style={{
                background: theme.cardBg, border: `1px solid ${theme.cardBorder}`,
                borderRadius: "14px", padding: "32px", marginBottom: "32px",
                textAlign: "center", boxShadow: theme.cardShadow,
              }}>
                <p style={{ fontSize: "18px", fontWeight: "700", color: theme.textPrimary, marginBottom: "8px" }}>
                  🔒 Your free trial has expired
                </p>
                <p style={{ fontSize: "14px", color: theme.textSecondary, marginBottom: "20px" }}>
                  Subscribe to unlock profit tracking, low stock alerts, reports and more.
                </p>
                <Link to="/subscription" style={{
                  display: "inline-block",
                  background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                  color: "#0a0f0f", borderRadius: "10px", padding: "12px 28px",
                  fontSize: "14px", fontWeight: "700", textDecoration: "none",
                }}>
                  View Plans
                </Link>
              </div>
            )}

            {/* Quick Actions */}
            <div style={{ marginBottom: "40px" }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: theme.textPrimary, marginBottom: "16px" }}>
                Quick Actions
              </h2>
              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <Link
                  to="/products"
                  style={{
                    background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                    color: "#0a0f0f",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  + Add Product
                </Link>
                <Link
                  to="/sales"
                  style={{
                    background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                    color: "#0a0f0f",
                    padding: "12px 24px",
                    borderRadius: "10px",
                    textDecoration: "none",
                    fontSize: "14px",
                    fontWeight: "700",
                  }}
                >
                  + Record Sale
                </Link>
                {hasFullAccess && (
                  <Link
                    to="/reports"
                    style={{
                      background: "linear-gradient(135deg, #2ec4b6, #1a9e92)",
                      color: "#0a0f0f",
                      padding: "12px 24px",
                      borderRadius: "10px",
                      textDecoration: "none",
                      fontSize: "14px",
                      fontWeight: "700",
                    }}
                  >
                    View Reports
                  </Link>
                )}
              </div>
            </div>
          </>
        )}
      </>
    </Layout>
  );
}
